import React, { useContext, useState, useEffect } from "react";
import {
  Grid2 as Grid,
  Stack,
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import LinearProgress from "@mui/material/LinearProgress";
import { useMutation } from "@apollo/client";
//Icons
import { BiMinusCircle } from "react-icons/bi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

//Srcs
import "../../Style/dialogStyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { translateLauguage } from "../../Function/Translate";
import { UPDATE_USER_PWD } from "../../Schema/Users";

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}
LinearProgressWithLabel.propTypes = {
  value: PropTypes.number.isRequired,
};

export const UpdatePassword = ({ open, handleClose, editData, setRefetch }) => {
  // console.log("editData==>", editData);

  //hiden and show vibility
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  //======================== update User =========================
  const [updateUserPassword] = useMutation(UPDATE_USER_PWD, {
    onCompleted: ({ updateUserPassword }) => {
      setLoading(false);
      if (updateUserPassword?.is_success === true) {
        setAlert(true, "success", updateUserPassword?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateUserPassword?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      console.log("error:::", error);
      setAlert(true, "error", error?.message);
    },
  });

  const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

  //fromik
  const CheckValidation = Yup.object().shape({
    password: Yup.string().required(t("require")).matches(passwordRules, {
      message: "Please enter a stronger password!",
    }),
    confirm_password: Yup.string()
      .required("Please retype your password!")
      .oneOf([Yup.ref("password")], "Your passwords do not match!"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirm_password: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (submitValues) => {
      setLoading(true);
      updateUserPassword({
        variables: {
          id: editData?._id,
          input: {
            password:submitValues?.password,
          },
        },
      });
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {t("dialog_title_reset_pwd_user")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_pwd")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_pwd")}
                  {...getFieldProps("password")}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password}
                  type={show ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start" onClick={handleClick}>
                        {show ? (
                          <VisibilityIcon
                            className="viewpw-icon"
                            sx={{ cursor: "pointer" }}
                          />
                        ) : (
                          <VisibilityOffIcon
                            className="viewpw-icon"
                            sx={{ cursor: "pointer" }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_cfpwd")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_cfpwd")}
                  type={show ? "text" : "password"}
                  {...getFieldProps("confirm_password")}
                  error={Boolean(
                    touched.confirm_password && errors.confirm_password
                  )}
                  helperText={
                    touched.confirm_password && errors.confirm_password
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start" onClick={handleClick}>
                        {show ? (
                          <VisibilityIcon
                            className="viewpw-icon"
                            sx={{ cursor: "pointer" }}
                          />
                        ) : (
                          <VisibilityOffIcon
                            className="viewpw-icon"
                            sx={{ cursor: "pointer" }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
            >
              {t("btn-update")}
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
